import { ref, computed, watch } from "vue";
import Permissions from "~/types/Permissions";

export interface SearchResult {
  id: string;
  label: string;
  description?: string;
  icon: string;
  route: string;
  category: string;
}

export type GroupedSearchResults = Record<string, SearchResult[]>;

interface CategoryConfig {
  /** Clé renvoyée par l'API `/api/global-search`. */
  key: string;
  /** Libellé affiché (= clé de `results`, groupe dans le composant). */
  category: string;
  icon: string;
  /**
   * Permission requise pour voir la catégorie (double garde avec le backend).
   * Typé via l'enum pour qu'une permission inexistante casse la compilation :
   * une chaîne libre absente du registre filtre silencieusement la catégorie.
   */
  permission: Permissions;
}

// L'ordre de ce tableau détermine l'ordre d'affichage des groupes de résultats.
// Chaque permission reprend celle qui garde déjà la liste correspondante, entrée
// de navigation ou page d'index selon la catégorie. Exception : Factures, où la
// navigation et les pages utilisent app.invoices.list ; la recherche suit le contrôle
// du backend (GlobalSearchController), qui exige app.invoices.view.
const CATEGORIES: CategoryConfig[] = [
  { key: "cities", category: "Villes", icon: "pi pi-map-marker", permission: Permissions.CitiesList },
  { key: "markets", category: "Marchés", icon: "pi pi-shop", permission: Permissions.MarketsList },
  { key: "merchants", category: "Commerçants", icon: "pi pi-user", permission: Permissions.MerchantsList },
  { key: "companies", category: "Entreprises", icon: "pi pi-building", permission: Permissions.CompaniesList },
  { key: "placers", category: "Placiers", icon: "pi pi-id-card", permission: Permissions.PlacersList },
  { key: "invoices", category: "Factures", icon: "pi pi-file", permission: Permissions.InvoicesView },
];

/** Icône par libellé de catégorie, pour l'en-tête de groupe du composant. */
export const categoryIcons: Record<string, string> = Object.fromEntries(
  CATEGORIES.map(c => [c.category, c.icon]),
);

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 500;

interface ApiItem {
  id: number;
  label: string;
  route: string;
}

type ApiResponse = Partial<Record<string, ApiItem[]>>;

export const useGlobalSearch = () => {
  const client = useSanctumClient();
  const route = useRoute();
  const { $hasPermission } = useNuxtApp();

  const query = ref("");
  const results = ref<GroupedSearchResults>({});
  const isOpen = ref(false);
  const isLoading = ref(false);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  // Compteur de requêtes : on n'applique que la réponse de la dernière frappe.
  let requestId = 0;

  const totalCount = computed(() =>
    Object.values(results.value).reduce((sum, items) => sum + items.length, 0),
  );
  const hasResults = computed(() => totalCount.value > 0);

  function tenantPrefix(): string {
    const tenant = route.params.tenant;
    return typeof tenant === "string" && tenant.length > 0 ? `/${tenant}` : "";
  }

  function canView(permission: Permissions): boolean {
    // Le backend filtre déjà par permission ; ce contrôle est une garde
    // défensive côté FO. Si le helper n'est pas disponible, on fait confiance
    // au backend.
    return typeof $hasPermission === "function" ? $hasPermission(permission) : true;
  }

  function mapResponse(data: ApiResponse): GroupedSearchResults {
    const grouped: GroupedSearchResults = {};
    const prefix = tenantPrefix();

    for (const config of CATEGORIES) {
      const items = data[config.key];
      if (!items || items.length === 0) continue;
      if (!canView(config.permission)) continue;

      grouped[config.category] = items.map(item => ({
        id: `${config.key}-${item.id}`,
        label: item.label,
        icon: config.icon,
        category: config.category,
        route: `${prefix}${item.route}`,
      }));
    }

    return grouped;
  }

  async function runSearch(term: string) {
    const currentRequest = ++requestId;
    try {
      const data = await client<ApiResponse>("/api/global-search", {
        query: { q: term },
      });
      if (currentRequest !== requestId) return; // réponse obsolète ignorée
      results.value = mapResponse(data ?? {});
    }
    catch (error) {
      if (currentRequest !== requestId) return;
      results.value = {};
      console.error("Global search failed:", error);
    }
    finally {
      if (currentRequest === requestId) {
        isLoading.value = false;
      }
    }
  }

  watch(query, (value) => {
    const term = value.trim();

    if (debounceTimer) clearTimeout(debounceTimer);

    // Toute frappe invalide la requête en vol et les résultats affichés : ils
    // portent sur un terme que l'utilisateur vient de quitter. Sans ça, la
    // réponse de « ab » peut encore s'appliquer alors que l'input contient
    // « abc », et l'utilisateur peut ouvrir une entité étrangère à sa saisie.
    requestId++;
    results.value = {};

    if (term.length < MIN_QUERY_LENGTH) {
      isLoading.value = false;
      return;
    }

    isLoading.value = true; // feedback immédiat (pendant debounce + requête)
    debounceTimer = setTimeout(() => runSearch(term), DEBOUNCE_MS);
  });

  function open() {
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
  }

  function clear() {
    query.value = "";
    results.value = {};
    isLoading.value = false;
  }

  return { query, results, hasResults, totalCount, isOpen, isLoading, open, close, clear };
};
